import {Component, computed, inject, input, linkedSignal, OnInit, output, resource, signal} from "@angular/core";
import {
	ApiError,
	Event,
	EventState,
	ExamFlow,
	ExamFlowRunMode,
	ExamSession,
	Modules,
	Permissions,
	ReplicationStateSession,
	SessionState,
	SessionType,
} from "@models";
import {zoomInOnEnterAnimation} from "angular-animations";
import {
	NotificationService,
	SimConfigurationService,
	SimExamFlowService,
	SimMaterialDialogService,
	SimSessionService,
	SimStudentPortalService,
	SimUtilityService,
} from "@services";
import {Translations} from "@translations";
import {EventEmitterService} from "@event-emitter";
import {Router} from "@angular/router";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {SimStatusPalette} from "src/app/shared/models/SimStatusPalette";
import {DatePipe} from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {MatDivider} from "@angular/material/divider";
import {MatTooltip} from "@angular/material/tooltip";
import {MatButton} from "@angular/material/button";
import {HasPermissionDirective} from "@common/directives/perm-directives/has-permission.directive";
import {SimButton} from "@common/ui/button/sim-button.component";
import {BusyDirective} from "@common/directives/busy.directive";
import {ActionMenuComponent} from "@common/ui/action-menu/action-menu.component";
import {examRoutes, routes} from "../../../../../../../environments/routes";
import {SessionStatusTagComponent} from "@common/components/session/session-status-tag/session-status-tag.component";
import {SessionStationPreviewComponent} from "@common/components/session/session-station-preview/session-station-preview.component";
import {ExamSessionCardStationContentComponent} from "@common/components/session/session-card/session-type/exam/content/exam-session-card-station-content.component";
import {EnumLabel, EnumLabelPipe} from "../../../../../../shared/shared-pipes/enum-label.pipe";
import {SessionReplicationStateTagComponent} from "@common/components/session/session-replication-state-tag/session-replication-state-tag.component";
import {TimeRangeLabelPipe} from "@common/pipes/time/time-range-label.pipe";

@Component({
	selector: "sim-exam-session-card",
	templateUrl: "./exam-session-card.component.html",
	styleUrls: ["./exam-session-card.component.scss"],
	animations: [zoomInOnEnterAnimation({duration: 175})],
	imports: [
		DatePipe,
		ActionMenuComponent,
		MatIcon,
		MatDivider,
		HasPermissionDirective,
		SimButton,
		MatTooltip,
		BusyDirective,
		TranslateModule,
		MatButton,
		ExamSessionCardStationContentComponent,
		SessionStatusTagComponent,
		SessionStationPreviewComponent,
		EnumLabelPipe,
		SessionReplicationStateTagComponent,
		TimeRangeLabelPipe,
	],
})
export class ExamSessionCardComponent implements OnInit {
	// I/O
	public readonly event = input.required<Event>();
	public readonly inputSession = input.required<ExamSession>({alias: "session"});
	public readonly showActionMenu = input<boolean>(false);
	public readonly disableControls = input<boolean>(false);
	public readonly isArchiveView = input<boolean>(false);
	public readonly isExamSchedule = input<boolean>(false);
	public readonly onEdit = output<void>();
	public readonly onDelete = output<void>();

	// Properties
	public sessionReplicationState = signal<ReplicationStateSession | null>(null);
	public sessionReplicationStateError = signal<string | null>(null);
	public busyPromise = signal<Promise<unknown> | null>(null);
	public isStudentPortalAvailable = signal<boolean>(SimConfigurationService.settings.studentPortalEnabled);

	// Constants
	protected readonly SessionType = SessionType;
	protected readonly SessionState = SessionState;
	protected readonly Modules = Modules;
	protected readonly SimStatusPalette = SimStatusPalette;
	protected readonly Permissions = Permissions;
	protected readonly T = Translations;
	protected readonly EnumLabel = EnumLabel;
	protected readonly ExamFlowRunMode = ExamFlowRunMode;
	protected readonly Math = Math;

	// Derived
	public session = linkedSignal<ExamSession>(() => this.inputSession());
	public canOpenMasterOfCeremony = computed<boolean>(() =>
		[SessionState.ExamConfirmationRunning, SessionState.ExamConfirmationClosed, SessionState.ExamRunning].includes(
			this.session().state,
		),
	);
	public canOpenConfirmation = computed<boolean>(() =>
		[SessionState.ExamConfirmationRunning, SessionState.ExamConfirmationClosed].includes(this.session().state),
	);
	public canStartSession = computed<boolean>(
		() => this.event()?.state === EventState.Active && this.session().state === SessionState.Scheduled,
	);
	public canCancelSession = computed<boolean>(
		() => ![SessionState.Scheduled, SessionState.Aborted, SessionState.Finished].includes(this.session().state),
	);
	public startExamSessionErrorTooltip = computed<string>(() => this.computeErrorTooltip(this.session()));
	public closingStart = computed<Date>(() => this.computeClosingStart(this.session()));
	public confirmationEnd = computed<Date>(() => this.computeConfirmationEnd(this.session()));
	readonly examFlow = resource<ExamFlow, string>({
		params: () => this.inputSession().examFlowId,
		loader: ({params}) => this.#examFlowService.getExamFlowById(params),
	});

	// Injections
	public readonly translate = inject(TranslateService);
	readonly #dialogService = inject(SimMaterialDialogService);
	readonly #notificationService = inject(NotificationService);
	readonly #sessionService = inject(SimSessionService);
	readonly #eventEmitter = inject(EventEmitterService);
	readonly #examFlowService = inject(SimExamFlowService);
	readonly #router = inject(Router);
	readonly #studentPortalService = inject(SimStudentPortalService);

	public ngOnInit(): void {
		if (this.isStudentPortalAvailable() && this.isArchiveView()) {
			this.#studentPortalService
				.getReplicationStateForSession(this.session()?.id)
				.then((replicationState) => this.sessionReplicationState.set(replicationState))
				.catch((_) => this.sessionReplicationStateError.set("Error getting replication state"));
		}

		//Subscribe to session updates
		this.#eventEmitter.onTrainingSessionUpdated.subscribe((updatedSessionEvent) => {
			if (updatedSessionEvent.simSession.id === this.session()?.id) {
				this.session.set(updatedSessionEvent.simSession as ExamSession);
			}
		});
	}

	protected async openConfirmationApplication() {
		await this.#router.navigate([`${examRoutes.confirmationApp}/${this.session().id}`]);
	}

	protected async cancelSession(): Promise<void> {
		const {action} = await this.#dialogService.openConfirmationDialog({
			title: this.T.CONFIRMATION_DIALOG_ABORT_SESSION_TITLE,
			cancelText: this.T.BUTTON_CANCEL,
			description: this.T.CONFIRMATION_DIALOG_ABORT_SESSION_DESC,
			okText: this.T.BUTTON_OK,
		});
		if (action === "ok") {
			await this.#sessionService.abortSession({sessionId: this.session().id});
			this.#notificationService.notifySuccess(this.translate.instant(Translations.CONFIRMATION_DIALOG_ABORT_SESSION_SUCCESS));
		}
	}

	protected startExamSession(): void {
		const promise = this.#sessionService.startExamSession({sessionId: this.session().id});
		this.busyPromise.set(promise);
		promise.catch((error: ApiError) => {
			if (!error.wasLogged) {
				this.#notificationService.notifyError(`Unexpected error happened while starting session!`);
			}
		});
	}

	protected openMasterOfCeremonyApp(): void {
		this.#router.navigate([`${routes.routes.masterOfCeremonyApp}/exam`, this.session().id]).then();
	}

	protected openExamSession(): void {
		const url = this.isExamSchedule() ? "/web-manager/list/exam-schedule" : "/web-manager/list/exam-archive/";
		this.#router.navigate([url, this.session().id]).then();
	}

	protected confirmDelete(): void {
		this.#dialogService
			.openDeleteDialog({
				deleteTitle: this.translate.instant(this.T.SESSION_SELECTION_DELETE_CREATED_SESSION),
				deleteText: this.translate.instant(this.T.SESSION_SELECTION_DELETE_CREATED_SESSION_TXT),
				deleteButtonText: this.translate.instant(this.T.BUTTON_DELETE),
				cancelText: this.translate.instant(this.T.BUTTON_CANCEL),
			})
			.then(({action}) => {
				if (action === "ok") {
					this.onDelete.emit();
				}
			});
	}

	protected async openSessionReplicationSettingsDialog() {
		await this.#dialogService.openSessionReplicationSettingsDialog({
			sessionId: this.session().id,
			replicationState: this.sessionReplicationState()!,
		});
	}

	protected openExamFlowDetailDialog() {
		this.#dialogService.openExamFlowDetailDialog({examFlow: this.examFlow.value()!}).then();
	}

	protected openEdit(): Function {
		return () => this.onEdit.emit();
	}

	private computeClosingStart(examSession: ExamSession): Date {
		const result = new Date(examSession.displayEndTime);
		result.setMinutes(result.getMinutes() - examSession.closing.durationInSeconds / 60);
		return result;
	}

	private computeErrorTooltip(examSession: ExamSession) {
		const startTime = examSession?.startTime;
		if (!!startTime && !SimUtilityService.toDayJs(startTime)?.isSame(new Date(), "day")) {
			return this.T.EVENT_SESSION_START_DISABLED_NOT_SAME_DAY;
		}
		return "";
	}

	private computeConfirmationEnd(examSession: ExamSession): Date {
		const result = new Date(examSession.startTime);
		result.setMinutes(result.getMinutes() + examSession.confirmation.durationInSeconds / 60);
		return result;
	}
}
